/**
 * Ongoing monitoring — enrolling a finished report's subjects into a standing
 * subscription.
 *
 * Mirrors `vnext/services/monitoring/schemas.py`. The wire shapes are
 * `Api*`-prefixed and converted by `transformTo*`, as everywhere else in
 * `src/types/`.
 */

/** What may be enrolled from one report, or why nothing may. */
export interface ApiMonitoringEnrolmentOptions {
  report_id: string;
  available: boolean;
  blocked_reason: string | null;
  subjects: ApiMonitoringEnrolmentSubject[];
  component_slugs: string[];
}

export interface ApiMonitoringEnrolmentSubject {
  subject_id: string;
  name: string;
  name_source: string;
  monitored_subject_id?: string | null;
  monitored_status?: MonitoredSubjectStatus | null;
  already_monitored: boolean;
}

export interface ApiMonitoredSubject {
  id: string;
  subject_id: string | null;
  name: string;
  status: string;
  scope_component_slugs: string[];
}

export interface ApiMonitoringEnrolmentResult {
  id: string;
  report_id: string;
  state: string;
  source: string;
  subjects: ApiMonitoredSubject[];
  registration_error?: string | null;
  baseline_failures?: string[];
}

/** A subject the report can enrol, and the name that would be registered. */
export interface MonitoringEnrolmentSubject {
  subjectId: string;
  name: string;
  /**
   * `selected_candidate` when identification confirmed the name, `subject`
   * when it falls back to what was typed at intake. Shown to the analyst
   * rather than hidden: registering an unverified intake name is exactly the
   * thing worth catching before a subscription starts billing.
   */
  nameSource: string;
  /** The team's existing subscription for this subject, if it has one. */
  monitoredSubjectId: string | null;
  /**
   * That subscription's status, or `null` when there is none. Any status makes
   * the subject unselectable — a second subscription is a second charge — but
   * `pending` means Castellum has not accepted it yet: something to retry, not
   * a subject that is covered.
   */
  monitoredStatus: MonitoredSubjectStatus | null;
}

export interface MonitoringEnrolmentOptions {
  reportId: string;
  available: boolean;
  /** Populated only when `available` is false. Safe to show to an analyst. */
  blockedReason: string | null;
  subjects: MonitoringEnrolmentSubject[];
  componentSlugs: string[];
}

export interface MonitoredSubject {
  id: string;
  subjectId: string | null;
  name: string;
  /**
   * `pending` until the provider has accepted the registration. It is not an
   * error state — enrolment commits before registering, so a provider outage
   * leaves a retryable subscription rather than losing the analyst's
   * selection.
   */
  status: string;
  scopeComponentSlugs: string[];
}

export interface MonitoringEnrolmentResult {
  id: string;
  reportId: string;
  state: string;
  source: string;
  subjects: MonitoredSubject[];
  /**
   * Castellum would not take the subjects. The enrolment still exists and its
   * subjects are pending; the worker retries them.
   */
  registrationError: string | null;
  /** Registered, but the opening screen did not run. */
  baselineFailures: string[];
}

export function transformToMonitoringEnrolmentSubject(
  api: ApiMonitoringEnrolmentSubject
): MonitoringEnrolmentSubject {
  return {
    subjectId: api.subject_id,
    name: api.name,
    nameSource: api.name_source,
    monitoredSubjectId: api.monitored_subject_id ?? null,
    // An API older than the status field sends only the flag. Reading it as
    // active keeps the subject unselectable, as it was.
    monitoredStatus: api.monitored_status ?? (api.already_monitored ? 'active' : null),
  };
}

export function transformToMonitoringEnrolmentOptions(
  api: ApiMonitoringEnrolmentOptions
): MonitoringEnrolmentOptions {
  return {
    reportId: api.report_id,
    available: api.available,
    blockedReason: api.blocked_reason,
    subjects: (api.subjects ?? []).map(transformToMonitoringEnrolmentSubject),
    componentSlugs: api.component_slugs ?? [],
  };
}

export function transformToMonitoredSubject(api: ApiMonitoredSubject): MonitoredSubject {
  return {
    id: api.id,
    subjectId: api.subject_id,
    name: api.name,
    status: api.status,
    scopeComponentSlugs: api.scope_component_slugs ?? [],
  };
}

export function transformToMonitoringEnrolmentResult(
  api: ApiMonitoringEnrolmentResult
): MonitoringEnrolmentResult {
  return {
    id: api.id,
    reportId: api.report_id,
    state: api.state,
    source: api.source,
    subjects: (api.subjects ?? []).map(transformToMonitoredSubject),
    registrationError: api.registration_error ?? null,
    baselineFailures: api.baseline_failures ?? [],
  };
}

/**
 * Display names for the components a client can monitor.
 *
 * Keyed by the backend's data-leaf slugs — `sanctions`, not
 * `sanctions-section`. A slug with no entry falls back to itself rather than
 * rendering blank, so a component added server-side is ugly here rather than
 * invisible.
 */
export const MONITORING_COMPONENT_LABELS: Record<string, string> = {
  sanctions: 'Sanctions',
  peps: 'PEPs',
  'adverse-media': 'Adverse media',
};

export function monitoringComponentLabel(slug: string): string {
  return MONITORING_COMPONENT_LABELS[slug] ?? slug;
}

/** One row of the analyst alert queue. */
export interface ApiMonitoringAlertRow {
  id: string;
  monitored_subject_id: string;
  subject_name: string;
  scope_component_slug: string;
  provider: string;
  state: string;
  severity: string | null;
  taxonomy_category: string | null;
  entry_identity: string;
  first_seen_at: string | null;
  last_seen_at: string | null;
  assigned_to_user_id: string | null;
  seeded: boolean;
  subject_status: MonitoredSubjectStatus;
}

export interface ApiMonitoringAlertPage {
  items: ApiMonitoringAlertRow[];
  total: number;
  version: string;
}

export interface MonitoringAlertRow {
  id: string;
  monitoredSubjectId: string;
  subjectName: string;
  scopeComponentSlug: string;
  provider: string;
  state: string;
  /** Null while classification has not completed. Routed as if high. */
  severity: string | null;
  taxonomyCategory: string | null;
  entryIdentity: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  assignedToUserId: string | null;
  /**
   * Came from the enrolling report rather than from monitoring. Excluded from
   * the queue unless asked for — these were adjudicated in the report and
   * number in the hundreds per enrolment.
   */
  seeded: boolean;
  /**
   * The monitored subject's status. Anything but `active` means the client is
   * not told about this alert — routing holds back every event on a
   * suspended, cancelled or pending subject.
   */
  subjectStatus: MonitoredSubjectStatus;
}

export interface MonitoringAlertPage {
  items: MonitoringAlertRow[];
  total: number;
  version: string;
}

export interface MonitoringAlertQuery {
  page?: number;
  pageSize?: number;
  state?: string;
  severity?: string;
  scopeComponentSlug?: string;
  monitoredSubjectId?: string;
  includeSeeded?: boolean;
}

export function transformToMonitoringAlertRow(api: ApiMonitoringAlertRow): MonitoringAlertRow {
  return {
    id: api.id,
    monitoredSubjectId: api.monitored_subject_id,
    subjectName: api.subject_name,
    scopeComponentSlug: api.scope_component_slug,
    provider: api.provider,
    state: api.state,
    severity: api.severity,
    taxonomyCategory: api.taxonomy_category,
    entryIdentity: api.entry_identity,
    firstSeenAt: api.first_seen_at,
    lastSeenAt: api.last_seen_at,
    assignedToUserId: api.assigned_to_user_id,
    seeded: api.seeded,
    // Missing only from an API older than this field. Treated as active, so
    // an older backend shows no flag rather than a false one.
    subjectStatus: api.subject_status ?? 'active',
  };
}

export function transformToMonitoringAlertPage(api: ApiMonitoringAlertPage): MonitoringAlertPage {
  return {
    items: (api.items ?? []).map(transformToMonitoringAlertRow),
    total: api.total ?? 0,
    version: api.version ?? '',
  };
}

/** Display labels for alert states. */
export const MONITORING_ALERT_STATE_LABELS: Record<string, string> = {
  new: 'New',
  confirmed: 'Confirmed',
  dismissed_no_match: 'Not our subject',
  dismissed_immaterial: 'Not material',
  resolved: 'Resolved',
};

export function monitoringAlertStateLabel(state: string): string {
  return MONITORING_ALERT_STATE_LABELS[state] ?? state;
}

// ---------------------------------------------------------------------------
// Entitlements — whether a team's monitoring is live
// ---------------------------------------------------------------------------

/** `active`, `suspended` or `ended`. A team with no row has never had monitoring. */
export type MonitoringEntitlementState = 'active' | 'suspended' | 'ended';

export interface ApiMonitoringEntitlement {
  id: string;
  team_id: string;
  state: MonitoringEntitlementState;
  contract_ref: string | null;
  notes: string | null;
  suspended_at: string | null;
  updated_by_user_id: string | null;
  updated_at: string | null;
}

export interface MonitoringEntitlement {
  id: string;
  teamId: string;
  state: MonitoringEntitlementState;
  contractRef: string | null;
  notes: string | null;
  suspendedAt: string | null;
  updatedByUserId: string | null;
  updatedAt: string | null;
}

export interface ApiMonitoringEntitlementChange {
  entitlement: ApiMonitoringEntitlement;
  subjects_changed: number;
  deregistered: number;
  deregistration_failures: string[];
  registered: number;
  registration_error: string | null;
  baseline_failures: string[];
  complete: boolean;
}

/**
 * What a suspend, resume or end did. The state change always lands; what can
 * fail is the provider half, and `complete: false` means billing or monitoring
 * did not follow it yet.
 */
export interface MonitoringEntitlementChange {
  entitlement: MonitoringEntitlement;
  subjectsChanged: number;
  deregistered: number;
  /** Non-empty means the provider is still billing for those subjects. */
  deregistrationFailures: string[];
  registered: number;
  /** Set when resuming could not re-register; those subjects stay pending. */
  registrationError: string | null;
  baselineFailures: string[];
  complete: boolean;
}

export type MonitoringEntitlementAction = 'suspend' | 'resume' | 'end';

export function transformToMonitoringEntitlement(
  api: ApiMonitoringEntitlement
): MonitoringEntitlement {
  return {
    id: api.id,
    teamId: api.team_id,
    state: api.state,
    contractRef: api.contract_ref,
    notes: api.notes,
    suspendedAt: api.suspended_at,
    updatedByUserId: api.updated_by_user_id,
    updatedAt: api.updated_at,
  };
}

export function transformToMonitoringEntitlementChange(
  api: ApiMonitoringEntitlementChange
): MonitoringEntitlementChange {
  return {
    entitlement: transformToMonitoringEntitlement(api.entitlement),
    subjectsChanged: api.subjects_changed ?? 0,
    deregistered: api.deregistered ?? 0,
    deregistrationFailures: api.deregistration_failures ?? [],
    registered: api.registered ?? 0,
    registrationError: api.registration_error ?? null,
    baselineFailures: api.baseline_failures ?? [],
    complete: api.complete,
  };
}

export const MONITORING_ENTITLEMENT_STATE_LABELS: Record<MonitoringEntitlementState, string> = {
  active: 'Active',
  suspended: 'Suspended',
  ended: 'Ended',
};

// ---------------------------------------------------------------------------
// Monitored subjects (admin list) and cancellation
// ---------------------------------------------------------------------------

export type MonitoredSubjectStatus = 'active' | 'pending' | 'suspended' | 'cancelled';

export interface ApiMonitoredSubjectRow {
  id: string;
  team_id: string;
  name: string;
  status: MonitoredSubjectStatus;
  name_source: string | null;
  monitoring_request_id: string;
  report_id: string | null;
  scope_component_slugs: string[];
  registered_upstream: boolean;
  enrolled_at: string | null;
  enrolled_by_user_id: string | null;
  cancelled_at: string | null;
  cancelled_by_user_id: string | null;
  cancellation_reason: MonitoringCancellationReason | null;
  cancellation_note: string | null;
}

export interface ApiMonitoredSubjectPage {
  items: ApiMonitoredSubjectRow[];
  total: number;
  version: string;
}

export interface MonitoredSubjectRow {
  id: string;
  teamId: string;
  name: string;
  status: MonitoredSubjectStatus;
  nameSource: string | null;
  monitoringRequestId: string;
  reportId: string | null;
  scopeComponentSlugs: string[];
  /**
   * Castellum may still hold the subject, and so may still be billing for it.
   * On a cancelled subject this means its removal has not gone through yet.
   */
  registeredUpstream: boolean;
  enrolledAt: string | null;
  enrolledByUserId: string | null;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  cancellationReason: MonitoringCancellationReason | null;
  cancellationNote: string | null;
}

export interface MonitoredSubjectPage {
  items: MonitoredSubjectRow[];
  total: number;
  version: string;
}

export interface MonitoredSubjectQuery {
  page?: number;
  pageSize?: number;
  teamId?: string;
  status?: MonitoredSubjectStatus;
  q?: string;
}

export type MonitoringCancellationReason =
  | 'client_request'
  | 'entitlement_ended'
  | 'wrong_subject'
  | 'duplicate'
  | 'other';

export interface ApiSubjectCancellation {
  subject: ApiMonitoredSubjectRow;
  deregistered: boolean;
  deregistration_failures: string[];
  complete: boolean;
}

export interface SubjectCancellation {
  subject: MonitoredSubjectRow;
  deregistered: boolean;
  /** Non-empty means Castellum still holds it; the worker retries the removal. */
  deregistrationFailures: string[];
  complete: boolean;
}

export function transformToMonitoredSubjectRow(api: ApiMonitoredSubjectRow): MonitoredSubjectRow {
  return {
    id: api.id,
    teamId: api.team_id,
    name: api.name,
    status: api.status,
    nameSource: api.name_source,
    monitoringRequestId: api.monitoring_request_id,
    reportId: api.report_id,
    scopeComponentSlugs: api.scope_component_slugs ?? [],
    registeredUpstream: api.registered_upstream,
    enrolledAt: api.enrolled_at,
    enrolledByUserId: api.enrolled_by_user_id,
    cancelledAt: api.cancelled_at,
    cancelledByUserId: api.cancelled_by_user_id,
    cancellationReason: api.cancellation_reason,
    cancellationNote: api.cancellation_note,
  };
}

export function transformToMonitoredSubjectPage(
  api: ApiMonitoredSubjectPage
): MonitoredSubjectPage {
  return {
    items: (api.items ?? []).map(transformToMonitoredSubjectRow),
    total: api.total ?? 0,
    version: api.version ?? '',
  };
}

export function transformToSubjectCancellation(api: ApiSubjectCancellation): SubjectCancellation {
  return {
    subject: transformToMonitoredSubjectRow(api.subject),
    deregistered: api.deregistered,
    deregistrationFailures: api.deregistration_failures ?? [],
    complete: api.complete,
  };
}

export interface ApiSubjectRegistration {
  subject: ApiMonitoredSubjectRow;
  registered: boolean;
  registration_error: string | null;
  baseline_failures: string[];
}

/** A pending subject after an admin asked for it to be registered now. */
export interface SubjectRegistration {
  subject: MonitoredSubjectRow;
  registered: boolean;
  /** Why Castellum still refused. The subject stays pending and is retried. */
  registrationError: string | null;
  baselineFailures: string[];
}

export function transformToSubjectRegistration(api: ApiSubjectRegistration): SubjectRegistration {
  return {
    subject: transformToMonitoredSubjectRow(api.subject),
    registered: api.registered,
    registrationError: api.registration_error ?? null,
    baselineFailures: api.baseline_failures ?? [],
  };
}

export const MONITORED_SUBJECT_STATUS_LABELS: Record<MonitoredSubjectStatus, string> = {
  active: 'Active',
  pending: 'Pending',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

export const CANCELLATION_REASON_LABELS: Record<MonitoringCancellationReason, string> = {
  client_request: 'Client asked to stop',
  entitlement_ended: 'Monitoring ended for the team',
  wrong_subject: 'Wrong subject',
  duplicate: 'Duplicate',
  other: 'Other',
};

/**
 * Reasons an admin may choose when cancelling one subject.
 *
 * `entitlement_ended` is left out: it is recorded by ending the team's
 * entitlement, and chosen for one subject it would claim a contract ended
 * that did not. The API refuses it too.
 */
export const SELECTABLE_CANCELLATION_REASONS: MonitoringCancellationReason[] = [
  'client_request',
  'wrong_subject',
  'duplicate',
  'other',
];

// ---------------------------------------------------------------------------
// What a client sees of their team's monitoring
// ---------------------------------------------------------------------------
//
// Separate from the admin shapes on purpose: the client API carries no review
// notes, model rationale or cancellation details, and neither do these.

export interface ApiTeamSubjectRow {
  id: string;
  name: string;
  status: MonitoredSubjectStatus;
  report_id: string | null;
  scope_component_slugs: string[];
  enrolled_at: string | null;
  sent_alert_count: number;
  last_sent_at: string | null;
}

export interface ApiTeamSubjectPage {
  items: ApiTeamSubjectRow[];
  total: number;
  version: string;
}

export interface ApiSentAlertEntry {
  name: string | null;
  list_source: string | null;
  title: string | null;
  url: string | null;
  published_date: string | null;
}

export interface ApiSentAlertRow {
  id: string;
  scope_component_slug: string;
  taxonomy_category: string | null;
  severity: string | null;
  last_sent_event: string | null;
  first_sent_at: string | null;
  last_sent_at: string | null;
  entry: ApiSentAlertEntry;
}

export interface ApiTeamSubjectDetail {
  subject: ApiTeamSubjectRow;
  alerts: ApiSentAlertRow[];
  alerts_total: number;
  version: string;
}

export interface TeamSubjectRow {
  id: string;
  name: string;
  status: MonitoredSubjectStatus;
  reportId: string | null;
  scopeComponentSlugs: string[];
  enrolledAt: string | null;
  /** Findings the team has actually been sent about this subject. */
  sentAlertCount: number;
  lastSentAt: string | null;
}

export interface TeamSubjectPage {
  items: TeamSubjectRow[];
  total: number;
  version: string;
}

export interface SentAlertEntry {
  name: string | null;
  listSource: string | null;
  title: string | null;
  url: string | null;
  publishedDate: string | null;
}

/** A finding the team was sent. */
export interface SentAlertRow {
  id: string;
  scopeComponentSlug: string;
  taxonomyCategory: string | null;
  severity: string | null;
  /** What the most recent message about it said happened. */
  lastSentEvent: string | null;
  firstSentAt: string | null;
  lastSentAt: string | null;
  entry: SentAlertEntry;
}

export interface TeamSubjectDetail {
  subject: TeamSubjectRow;
  alerts: SentAlertRow[];
  alertsTotal: number;
  version: string;
}

export interface TeamSubjectQuery {
  page?: number;
  pageSize?: number;
  status?: MonitoredSubjectStatus;
  q?: string;
}

export function transformToTeamSubjectRow(api: ApiTeamSubjectRow): TeamSubjectRow {
  return {
    id: api.id,
    name: api.name,
    status: api.status,
    reportId: api.report_id,
    scopeComponentSlugs: api.scope_component_slugs ?? [],
    enrolledAt: api.enrolled_at,
    sentAlertCount: api.sent_alert_count ?? 0,
    lastSentAt: api.last_sent_at,
  };
}

export function transformToTeamSubjectPage(api: ApiTeamSubjectPage): TeamSubjectPage {
  return {
    items: (api.items ?? []).map(transformToTeamSubjectRow),
    total: api.total ?? 0,
    version: api.version ?? '',
  };
}

export function transformToSentAlertRow(api: ApiSentAlertRow): SentAlertRow {
  return {
    id: api.id,
    scopeComponentSlug: api.scope_component_slug,
    taxonomyCategory: api.taxonomy_category,
    severity: api.severity,
    lastSentEvent: api.last_sent_event,
    firstSentAt: api.first_sent_at,
    lastSentAt: api.last_sent_at,
    entry: {
      name: api.entry?.name ?? null,
      listSource: api.entry?.list_source ?? null,
      title: api.entry?.title ?? null,
      url: api.entry?.url ?? null,
      publishedDate: api.entry?.published_date ?? null,
    },
  };
}

export function transformToTeamSubjectDetail(api: ApiTeamSubjectDetail): TeamSubjectDetail {
  return {
    subject: transformToTeamSubjectRow(api.subject),
    alerts: (api.alerts ?? []).map(transformToSentAlertRow),
    alertsTotal: api.alerts_total ?? 0,
    version: api.version ?? '',
  };
}

/** How a subject's status reads to a client — plainer than the admin labels. */
export const CLIENT_SUBJECT_STATUS_LABELS: Record<MonitoredSubjectStatus, string> = {
  active: 'Monitoring',
  pending: 'Starting',
  suspended: 'Paused',
  cancelled: 'Stopped',
};

/** What a sent message said happened. Unknown kinds fall back to "Update". */
export const SENT_EVENT_LABELS: Record<string, string> = {
  first_seen: 'New finding',
  changed_material: 'Significant change',
  changed_other: 'Updated',
};

export function sentEventLabel(kind: string | null): string {
  return (kind && SENT_EVENT_LABELS[kind]) || 'Update';
}

/** A category slug as words — `sanctions-designation` → "Sanctions designation". */
export function findingCategoryLabel(slug: string | null): string {
  if (!slug) {
    return 'Not yet categorised';
  }
  const words = slug.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const CLIENT_SUBJECT_STATUS_COLOURS: Record<MonitoredSubjectStatus, string> = {
  active: 'green',
  pending: 'blue',
  suspended: 'orange',
  cancelled: 'gray',
};

/**
 * An instant as a short local date: "11 Sep 2026". Local rather than UTC,
 * because these are moments something was sent or seen, not calendar days.
 */
export function formatMonitoringDate(value: string | null): string {
  if (!value) {
    return '-';
  }
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The URL, if it is safe to render as a link on a client's screen.
 *
 * It comes from the provider's list entry, so it is untrusted: anything but
 * http(s) — `javascript:`, `data:` — is dropped rather than made clickable.
 */
export function safeExternalUrl(value: string | null): string | null {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}
